import React, { useState, useEffect } from "react";
import { VARIABLES } from "../constants";
import styles from "../../../../styles/mailCenter/createTemplateModal.module.scss";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Image from "@tiptap/extension-image";
import axios from "axios";
import swal from "sweetalert";
import { isAuth } from "../../../../pages/auth/ProtectedRoute";
import type { Template } from "../constants";


interface RichToolbarProps {
  editor: ReturnType<typeof useEditor>;
}

const BoldIcon: React.FC = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
  >
    <path d="M6 4h8a4 4 0 0 1 4 4 4 4 0 0 1-4 4H6z" />
    <path d="M6 12h9a4 4 0 0 1 4 4 4 4 0 0 1-4 4H6z" />
  </svg>
);
const ItalicIcon: React.FC = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
  >
    <line x1="19" y1="4" x2="10" y2="4" />
    <line x1="14" y1="20" x2="5" y2="20" />
    <line x1="15" y1="4" x2="9" y2="20" />
  </svg>
);
const LinkIcon: React.FC = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
  >
    <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
    <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
  </svg>
);
const ImageIcon: React.FC = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
  >
    <rect x="3" y="3" width="18" height="18" rx="2" />
    <circle cx="8.5" cy="8.5" r="1.5" />
    <polyline points="21 15 16 10 5 21" />
  </svg>
);
const ListIcon: React.FC = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
  >
    <line x1="8" y1="6" x2="21" y2="6" />
    <line x1="8" y1="12" x2="21" y2="12" />
    <line x1="8" y1="18" x2="21" y2="18" />
    <line x1="3" y1="6" x2="3.01" y2="6" />
    <line x1="3" y1="12" x2="3.01" y2="12" />
    <line x1="3" y1="18" x2="3.01" y2="18" />
  </svg>
);

const StarIcon: React.FC<{ stroke?: string }> = ({ stroke = "#fff" }) => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M14.0013 5.33594C13.0346 5.33594 12.4946 6.29594 12.7146 7.00927L10.348 9.3826C10.148 9.3226 9.85464 9.3226 9.65464 9.3826L7.95464 7.6826C8.1813 6.96927 7.6413 6.0026 6.66797 6.0026C5.7013 6.0026 5.15464 6.9626 5.3813 7.6826L2.3413 10.7159C1.62797 10.4959 0.667969 11.0359 0.667969 12.0026C0.667969 12.7359 1.26797 13.3359 2.0013 13.3359C2.96797 13.3359 3.50797 12.3759 3.28797 11.6626L6.3213 8.6226C6.5213 8.6826 6.81464 8.6826 7.01464 8.6226L8.71464 10.3226C8.48797 11.0359 9.02797 12.0026 10.0013 12.0026C10.968 12.0026 11.5146 11.0426 11.288 10.3226L13.6613 7.95594C14.3746 8.17594 15.3346 7.63594 15.3346 6.66927C15.3346 5.93594 14.7346 5.33594 14.0013 5.33594Z"
      fill="#EFA148"
    />
    <path
      d="M10.0013 6L10.628 4.62L12.0013 4L10.628 3.38L10.0013 2L9.38797 3.38L8.0013 4L9.38797 4.62L10.0013 6ZM2.33464 7.33333L2.66797 6L4.0013 5.66667L2.66797 5.33333L2.33464 4L2.0013 5.33333L0.667969 5.66667L2.0013 6L2.33464 7.33333Z"
      fill="#EFA148"
    />
  </svg>
);

const AiIcon: React.FC<{ stroke?: string }> = ({ stroke = "#fff" }) => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <g clipPath="url(#clip0_11734_44530)">
      <path
        d="M6.62625 10.3361C6.56674 10.1054 6.44648 9.89484 6.278 9.72636C6.10952 9.55788 5.89897 9.43762 5.66825 9.3781L1.57825 8.32344C1.50847 8.30363 1.44706 8.2616 1.40333 8.20373C1.3596 8.14586 1.33594 8.07531 1.33594 8.00277C1.33594 7.93023 1.3596 7.85968 1.40333 7.80181C1.44706 7.74394 1.50847 7.70191 1.57825 7.6821L5.66825 6.62677C5.89889 6.56731 6.10938 6.44715 6.27786 6.2788C6.44633 6.11044 6.56663 5.90003 6.62625 5.66944L7.68092 1.57944C7.70053 1.50938 7.74251 1.44766 7.80047 1.4037C7.85843 1.35974 7.92917 1.33594 8.00192 1.33594C8.07467 1.33594 8.14542 1.35974 8.20337 1.4037C8.26133 1.44766 8.30332 1.50938 8.32292 1.57944L9.37692 5.66944C9.43644 5.90015 9.5567 6.1107 9.72518 6.27918C9.89366 6.44766 10.1042 6.56792 10.3349 6.62744L14.4249 7.68144C14.4953 7.70084 14.5573 7.74278 14.6015 7.80082C14.6457 7.85887 14.6696 7.92981 14.6696 8.00277C14.6696 8.07573 14.6457 8.14667 14.6015 8.20472C14.5573 8.26276 14.4953 8.3047 14.4249 8.3241L10.3349 9.3781C10.1042 9.43762 9.89366 9.55788 9.72518 9.72636C9.5567 9.89484 9.43644 10.1054 9.37692 10.3361L8.32225 14.4261C8.30265 14.4962 8.26067 14.5579 8.20271 14.6018C8.14475 14.6458 8.074 14.6696 8.00125 14.6696C7.92851 14.6696 7.85776 14.6458 7.7998 14.6018C7.74184 14.5579 7.69986 14.4962 7.68025 14.4261L6.62625 10.3361Z"
        stroke={stroke}
        strokeWidth="1.33333"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M13.332 2V4.66667"
        stroke={stroke}
        strokeWidth="1.33333"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M14.6667 3.33594H12"
        stroke={stroke}
        strokeWidth="1.33333"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M2.66797 11.3359V12.6693"
        stroke={stroke}
        strokeWidth="1.33333"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M3.33333 12H2"
        stroke={stroke}
        strokeWidth="1.33333"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </g>
    <defs>
      <clipPath id="clip0_11734_44530">
        <rect width="16" height="16" fill={stroke} />
      </clipPath>
    </defs>
  </svg>
);

const RichToolbar: React.FC<RichToolbarProps> = ({ editor }) => {
  if (!editor) return null;

  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const handleLink = () => {
    const url = window.prompt("Enter URL");
    if (url) editor.chain().focus().setLink({ href: url }).run();
  };

  const handleImage = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('files[0]', file);

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_USER_API}uploads/file`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${isAuth()}`,
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      if (response.status === 200 && response.data.status === 'success') {
        const filepath = response.data.data[0].filepath;
        const fullUrl = `${process.env.REACT_APP_IMAGE_API_PREFIX}${filepath}`;
        editor.chain().focus().setImage({ src: fullUrl }).run();
      } else {
        console.error('Upload failed:', response.data.message);
      }
    } catch (error) {
      console.error('Error uploading image:', error);
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className={styles["rich-toolbar"]}>
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileChange}
        accept="image/*"
        style={{ display: 'none' }}
      />
      <button
        type="button"
        onClick={() => editor.chain().focus().toggleBold().run()}
        className={`${styles["rich-toolbar__btn"]} ${editor.isActive("bold") ? styles["rich-toolbar__btn--active"] : ""}`}
      >
        <BoldIcon />
      </button>

      <button
        type="button"
        onClick={() => editor.chain().focus().toggleItalic().run()}
        className={`${styles["rich-toolbar__btn"]} ${editor.isActive("italic") ? styles["rich-toolbar__btn--active"] : ""}`}
      >
        <ItalicIcon />
      </button>

      <div className={styles["rich-toolbar__divider"]} />

      <button
        type="button"
        onClick={handleLink}
        className={styles["rich-toolbar__btn"]}
      >
        <LinkIcon />
      </button>
      <button
        type="button"
        onClick={handleImage}
        className={styles["rich-toolbar__btn"]}
      >
        <ImageIcon />
      </button>

      <div className={styles["rich-toolbar__divider"]} />

      <button
        type="button"
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        className={`${styles["rich-toolbar__btn"]} ${editor.isActive("bulletList") ? styles["rich-toolbar__btn--active"] : ""}`}
      >
        <ListIcon />
      </button>

      <span className={styles["rich-toolbar__hint"]}>
        <StarIcon stroke="#3E76CD" /> Use HTML tags for formatting
      </span>
    </div>
  );
};

const EditTemplateModal: React.FC<EditTemplateModalProps> = ({
  isOpen,
  onClose,
  template,
  onEditSuccess,
}) => {
  const [name, setName] = useState<string>("");
  const [subject, setSubject] = useState<string>("");
  const [body, setBody] = useState<string>("");
  const [bodyMode, setBodyMode] = useState<BodyMode>("Plain Text");
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [isOpen, onClose]);

  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) onClose();
  };

  const editor = useEditor({
    extensions: [StarterKit, Link.configure({ openOnClick: false }), Image],
    content: "",
    editorProps: {
      attributes: {
        "data-placeholder":
          "Write HTML content or use the toolbar above to format. Example:\n<h1>Hello {{first_name}}</h1>\n<p>Welcome to Kominiti!</p>",
      },
    },
  });

  const richBody = editor?.getHTML() ?? "";

  const isRichTextEmpty =
    !editor || editor.isEmpty || richBody === "<p></p>";

  const finalBody = bodyMode === "Plain Text" ? body : richBody;

  useEffect(() => {
    if (!isOpen || !template) {
      setName("");
      setSubject("");
      setBody("");
      setBodyMode("Plain Text");
      editor?.commands.clearContent();
      return;
    }

    // Populate form with template data
    setName(template.name || "");
    setSubject(template.subject || "");
    
    // Check if body is HTML/rich text or plain text
    const isRichText = template.body && template.body.includes("<");
    setBodyMode(isRichText ? "Rich Text" : "Plain Text");
    
    if (isRichText) {
      editor?.commands.setContent(template.body || "");
    } else {
      setBody(template.body || "");
    }
  }, [isOpen, template, editor]);

  const editTemplate = async () => {
    if (!template) return;

    setLoading(true);
    try {
      const richBody = editor?.getHTML() ?? "";
      if (!subject.trim() || !finalBody.trim()) return;

      const payload = {
        name,
        subject,
        body: finalBody,
        content_type: bodyMode === "Rich Text" ? "rich" : "plain",
      };

      const res = await axios.put(
        `${process.env.REACT_APP_HOST_API}mail-center/templates/${template.id}`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${isAuth()}`,
          },
        }
      );
      
      if (res.status === 200) {
        onClose();
        swal({
          title: "Success!",
          text: res?.data?.message || "Template updated successfully!",
          icon: "success",
        });
        onEditSuccess();
      }
    } catch (err: any) {
      console.log("error updating template:", err);
      swal({
        title: "Error!",
        text: err?.response?.data?.message || "Failed to update template",
        icon: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen || !template) return null;

  return (
    <div className={styles.overlay} onClick={handleOverlayClick}>
      <div className={styles.modal}>
        {/* Header */}
        <div className={styles["modal__header"]}>
          <h2 className={styles["modal__title"]}>Edit Template</h2>
          <button
            className={styles["modal__close-btn"]}
            onClick={onClose}
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        {/* Body */}
        <div className={styles["modal__body"]}>
          {/* name */}
          <div className={styles.field}>
            <label className={styles["field__label"]}>
              Name
            </label>
            <input
              className={styles["field__input"]}
              placeholder="Enter template name"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>

          {/* Subject */}
          <div className={styles.field}>
            <label className={styles["field__label"]}>
              Subject Line
              <span className={styles["field__label-required"]}>*</span>
            </label>
            <input
              className={styles["field__input"]}
              placeholder="e.g Welcome to Kominiti!"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
            />
          </div>

          {/* Suggested Variables */}
          <div className={styles.variables}>
            <div className={styles["variables__header"]}>
              <AiIcon stroke="#4895EF" />
              <span className={styles["variables__label"]}>
                Suggested Variables
              </span>
            </div>
            <div className={styles["variables__chips"]}>
              {VARIABLES.map((v) => (
                <span
                  key={v}
                  className={styles["variables__chip"]}
                  onClick={() => {
                    if (bodyMode === "Plain Text") {
                      setBody((prev) => prev + v);
                    } else {
                      editor?.chain().focus().insertContent(v).run();
                    }
                  }}
                >
                  {v}
                </span>
              ))}
            </div>
          </div>

          {/* Email Body */}
          <div>
            <div className={styles["body-header"]}>
              <label className={styles["body-header__label"]}>
                Email Body
                <span className={styles["body-header__label-required"]}>*</span>
              </label>
              <div className={styles["body-header__actions"]}>
                <button className={styles["body-header__ai-btn"]}>
                  <AiIcon /> AI Rewrite
                </button>
                {(["Plain Text", "Rich Text"] as BodyMode[]).map((mode) => (
                  <button
                    key={mode}
                    onClick={() => setBodyMode(mode)}
                    className={[
                      styles["body-header__mode-btn"],
                      bodyMode === mode
                        ? styles["body-header__mode-btn--active"]
                        : "",
                    ].join(" ")}
                  >
                    {mode}
                  </button>
                ))}
              </div>
            </div>
            {bodyMode === "Plain Text" ? (
              <textarea
                className={styles["field__textarea"]}
                placeholder="Write your email content here. Use variables like {{first_name}} for personalization."
                value={body}
                onChange={(e) => setBody(e.target.value)}
                rows={8}
              />
            ) : (
              <>
                <RichToolbar editor={editor} />
                <div className={styles["rich-editor"]}>
                  <EditorContent editor={editor} />
                </div>
              </>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className={styles["modal__footer"]}>
          <button className={styles["modal__cancel-btn"]} onClick={onClose}>
            Cancel
          </button>

          <button
            className={styles["modal__submit-btn"]}
            onClick={editTemplate}
            disabled={
              !subject.trim() ||
              loading ||
              (bodyMode === "Plain Text"
                ? !body.trim()
                : isRichTextEmpty)
            }
          >
            {loading ? 'Updating Template...' : 'Update Template'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditTemplateModal;
